// src/components/ReferralSystem.jsx
// Sistema de referidos — Crear código, compartir, ver estadísticas
import React, { useState, useEffect, useCallback } from 'react';

// ─── Modal para CREAR código después de una compra ──────────────────
export const ReferralCreateModal = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [createdCode, setCreatedCode] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!code.trim()) {
      setError('Escribí un código para compartir.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('/api/referrals?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), whatsapp }),
      });
      const data = await res.json();
      if (data.success) {
        setCreatedCode(data);
        localStorage.setItem('myReferralCode', data.code);
      } else {
        setError(data.error || 'Error al crear el código.');
      }
    } catch {
      setError('Error de conexión. Intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdCode.shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const text = `Te invito a descubrir tu lectura de tarot en Tarot Místico. Usá mi código ${createdCode.code} y accedé a lecturas con IA. ${createdCode.shareUrl}`;
    if (navigator.share) {
      navigator.share({ title: 'Tarot Místico', text, url: createdCode.shareUrl });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--referral" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-btn" aria-label="Cerrar">✕</button>

        {!createdCode ? (
          <>
            <div className="referral-header">
              <span className="referral-icon">🎁</span>
              <h2 className="font-cinzel referral-title">Ganá compartiendo</h2>
              <p className="referral-subtitle">
                Creá tu código único y ganá el <strong>20% de comisión</strong> por cada persona que compre con tu código.
              </p>
            </div>

            <div className="referral-form">
              <label className="referral-label">Tu código personalizado</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-_]/g, ''))}
                placeholder="Ej: LUNA, MARIA, TAROT2024"
                className="referral-input"
                maxLength={20}
              />
              <span className="referral-hint">3-20 caracteres: letras, números y guiones</span>

              <label className="referral-label" style={{ marginTop: '1rem' }}>Tu WhatsApp (opcional, para cobrar)</label>
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="Ej: +5492241563807"
                className="referral-input"
              />

              {error && <p className="referral-error">{error}</p>}

              <button
                onClick={handleCreate}
                disabled={isLoading || !code.trim()}
                className="main-button primary-button referral-btn"
              >
                {isLoading ? 'Creando...' : 'Crear mi código'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="referral-header">
              <span className="referral-icon">✅</span>
              <h2 className="font-cinzel referral-title">¡Código creado!</h2>
            </div>

            <div className="referral-success">
              <div className="referral-code-display">
                <span className="referral-code-value">{createdCode.code}</span>
              </div>
              <p className="referral-share-text">Compartí este link y ganá el 20% de cada venta:</p>
              <div className="referral-link-box">
                <span className="referral-link-url">{createdCode.shareUrl}</span>
                <button onClick={handleCopy} className="referral-copy-btn">
                  {copied ? '¡Copiado!' : 'Copiar'}
                </button>
              </div>

              <button onClick={handleShare} className="main-button primary-button referral-btn referral-share-btn">
                Compartir por WhatsApp
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Input de código referido ANTES de pagar ────────────────────────
export const ReferralCodeInput = ({ onCodeValidated }) => {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(''); // '', 'valid', 'invalid', 'checking'

  const validateCode = useCallback(async (codeToCheck) => {
    const clean = (codeToCheck || '').trim().toUpperCase();
    if (!clean || clean.length < 3) {
      setStatus('');
      onCodeValidated(null);
      return;
    }

    setStatus('checking');
    try {
      const res = await fetch(`/api/referrals?action=validate&code=${clean}`);
      const data = await res.json();
      if (data.valid) {
        setStatus('valid');
        onCodeValidated(clean);
      } else {
        setStatus('invalid');
        onCodeValidated(null);
      }
    } catch {
      setStatus('');
      onCodeValidated(null);
    }
  }, [onCodeValidated]);

  // Detectar código en URL (?ref=CODIGO)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setCode(refCode.toUpperCase());
      validateCode(refCode);
    }
  }, [validateCode]);

  const handleBlur = () => {
    if (code.trim().length >= 3) validateCode(code);
  };

  return (
    <div className="referral-input-section">
      <label className="referral-input-label">
        ¿Tenés un código de referido?
      </label>
      <div className="referral-input-row">
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-_]/g, ''));
            setStatus('');
          }}
          onBlur={handleBlur}
          placeholder="Ingresá el código"
          className={`referral-input referral-input--inline ${status === 'valid' ? 'referral-input--valid' : ''} ${status === 'invalid' ? 'referral-input--invalid' : ''}`}
          maxLength={20}
        />
        {status === 'checking' && <span className="referral-status">...</span>}
        {status === 'valid' && <span className="referral-status referral-status--valid">✓</span>}
        {status === 'invalid' && <span className="referral-status referral-status--invalid">✗</span>}
      </div>
      {status === 'valid' && <p className="referral-msg referral-msg--valid">¡Código válido!</p>}
      {status === 'invalid' && <p className="referral-msg referral-msg--invalid">Código no encontrado</p>}
    </div>
  );
};

// ─── Mini panel de estadísticas para el referente ────────────────────
export const ReferralStats = () => {
  const [stats, setStats] = useState(null);
  const myCode = localStorage.getItem('myReferralCode');

  useEffect(() => {
    if (!myCode) return;
    fetch(`/api/referrals?action=stats&code=${myCode}`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, [myCode]);

  if (!myCode || !stats) return null;

  return (
    <div className="referral-stats-card">
      <h3 className="font-cinzel referral-stats-title">Tu código: {stats.code}</h3>
      <div className="referral-stats-grid">
        <div className="referral-stat">
          <span className="referral-stat-value">{stats.totalReferrals}</span>
          <span className="referral-stat-label">Referidos</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">${stats.totalEarned.toLocaleString()}</span>
          <span className="referral-stat-label">Ganado (ARS)</span>
        </div>
      </div>
      <p className="referral-stats-contact">
        Para cobrar tu comisión escribinos a{' '}
        <a href="https://wa.me/5492241563807" target="_blank" rel="noopener noreferrer">
          WhatsApp
        </a>
      </p>
    </div>
  );
};
